import React, { useCallback, useMemo, useState } from 'react';

import {
  FullBOUserResponseModel,
  BOUserStatusEnum,
  BOUsersApproveStepModel,
  StageStatusEnum,
} from 'api/adminApiClient';
import PricingAgreementModal, {
  PricingAgreementType,
} from 'components/modals/PricingAgreementModal';
import {
  getProfileProgress,
  StepChangeRequestModel,
  DEFAULT_ACTION_BTN_TEXT,
  DEFAULT_ACTION_FORBIDDEN_MESSAGE,
} from 'utils/profileProgressUtils/generalProgressUtils';
import {
  BaseProgressStep,
  BaseProgressPercentage,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import BaseAlert from 'components/wrappers/BaseAlert';
import { LoadRecruiterDetailsOptions } from '../types';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { completeRecruiterProgressStep } from 'redux/reducers/recruiterSlice';
import { getMemberProgressStages } from 'utils/profileProgressUtils/memberProgressUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './RecruiterProgressBar.module.less';

type Props = {
  isRequest: boolean;
  recruiter: FullBOUserResponseModel;
  loadRecruiterDetails: (
    options?: LoadRecruiterDetailsOptions,
  ) => Promise<void>;
};

export default function RecruiterProgressBar(props: Props) {
  const { recruiter, loadRecruiterDetails, isRequest } = props;
  const { userId, stages } = recruiter;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const [isPricingAgreementModalOpen, setIsPricingAgreementModalOpen] =
    useState(false);
  const memberStages = useMemo(() => {
    const stagesOptions = {
      isAdmin,
      stages,
      isRequest,
    };

    return getMemberProgressStages(stagesOptions);
  }, [stages, isAdmin, isRequest]);

  const onStepChange = useCallback(
    async (stageData: StepChangeRequestModel) => {
      const { stage, stageStatus, message: requestMessage } = stageData;
      const data = {
        userId,
        currentStage: stage,
        currentStageStatus: stageStatus,
      } as BOUsersApproveStepModel;

      requestMessage && (data.message = requestMessage);

      const result = await dispatch(completeRecruiterProgressStep(data));

      if (completeRecruiterProgressStep.fulfilled.match(result)) {
        !!requestMessage && BaseAlert.success('Request sent.');

        const isPricingAgreementStage =
          stageData.stage === BOUserStatusEnum.PriceAgreementSent;

        await loadRecruiterDetails({
          shouldLoadPricingAgreementFile: isPricingAgreementStage,
        });
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [userId, dispatch, loadRecruiterDetails],
  );

  const handleStepChange = useCallback(
    async (stageData: StepChangeRequestModel) => {
      if (
        stageData.stage === BOUserStatusEnum.PriceAgreementSent &&
        stageData.stageStatus !== StageStatusEnum.SoftStopHalfCompleted
      ) {
        setIsPricingAgreementModalOpen(true);
        return;
      }

      await onStepChange(stageData);
    },
    [onStepChange],
  );

  return (
    <>
      {isPricingAgreementModalOpen && (
        <PricingAgreementModal
          agreementType={PricingAgreementType.Create}
          recruiter={recruiter}
          onAgreementSent={onStepChange}
          onClose={() => setIsPricingAgreementModalOpen(false)}
        />
      )}

      <div className={styles.progressTracker}>
        <div className={styles.progressPercentage}>
          <BaseProgressPercentage percent={getProfileProgress(memberStages)} />
        </div>

        <div className="page-box">
          <div className="page-inner-container">
            <BaseProgressTrackerWrapper>
              {memberStages.map((progressStep, index) => (
                <BaseProgressStep
                  key={index}
                  isAdmin
                  index={index}
                  {...progressStep}
                  errorText={
                    progressStep.errorText || DEFAULT_ACTION_FORBIDDEN_MESSAGE
                  }
                  onComplete={handleStepChange}
                  actionBtnText={
                    progressStep.actionBtnText || DEFAULT_ACTION_BTN_TEXT
                  }
                  isActionEnabled={progressStep.isActionEnabled || false}
                  onRequestSent={onStepChange}
                />
              ))}
            </BaseProgressTrackerWrapper>
          </div>
        </div>
      </div>
    </>
  );
}
